<template>
<span>
	<!-- 20260616 -- Copilot (Claude Sonnet 4.6) -- Removed inline style (position:sticky/relative + margin-bottom:125pt); spacer height is set dynamically by _applyNavbarHeight via ResizeObserver -->
	<b-row cols="12" class="usermenu-spacer-row">

		<b-col>

			<!-- 20260615 -- Copilot (Claude Sonnet 4.6) -- Removed b-collapse (the expanding collapse panel); kept toggleable="lg" -->
			<!-- WHY: toggleable="lg" adds navbar-expand-lg CSS class, which hides .navbar-toggler at ≥992px. -->
			<!-- Without it, navbar-expand hides the toggler at ALL widths, breaking the mobile hamburger. -->
			<b-navbar
            		toggleable="lg"
            		type="dark"
				fixed="top"
				class="usermenu-navbar expanded-navbar"
				:class="{ 'header-hidden': headerHidden }">

				<!-- Block 1: Mobile/tablet nav bar (hidden at lg+) -->
				<div class="d-flex d-lg-none mobile-nav-row w-100 align-items-center">
					<button
						class="navbar-toggler mobile-sidebar-toggle"
						type="button"
						@click="handleSidebarToggle"
						aria-label="Open navigation"
					>&#9776;</button>

					<img
						:src="require('@/assets/logos/PTLogo-SVG-NoText-Cropped.svg')"
						alt="Principal Task"
						class="header-logo mx-2"
					/>

					<!-- 20260619 -- Copilot (Claude Sonnet 4.6) -- Always render dropdown (not gated on getUser) so menu is -->
					<!-- accessible during initial load. Avatar src is guarded to avoid null dereference until user loads. -->
					<b-nav-item-dropdown
						right
						class="avatar-dropdown-widget ml-auto"
					>
						<template v-slot:button-content>
							<b-avatar
								:src="$store.getters.getUser ? $store.getters.getUser.picture : null"
								size="38px"
								class="header-avatar-widget"
							/>
						</template>
						<b-dropdown-item :to="{name: 'profile'}">
							<b-icon-person />&nbsp; Profile
						</b-dropdown-item>
						<!-- 20260616 -- Copilot (Claude Sonnet 4.6) -- Simplified v-if: (!loading && !auth) || !auth always equals !auth -->
						<b-dropdown-item
							v-if="!$auth.isAuthenticated"
							:to="{name: 'signup'}"
						>
							<b-icon-lock />&nbsp; Sign In
						</b-dropdown-item>
						<!-- 20260616 -- Copilot (Claude Sonnet 4.6) -- Removed href="#" to prevent hash navigation on logout -->
						<b-dropdown-item v-else @click="logout()">
							<b-icon-lock-fill />&nbsp; Log Out
						</b-dropdown-item>
					</b-nav-item-dropdown>
				</div>

				<!-- Block 2: Mobile/tablet stats strip (hidden at lg+) -->
				<!-- 20260615 -- Copilot (Claude Sonnet 4.6) -- Compact stats; taps use existing navigateToFilteredTasks -->
				<div
					class="d-flex d-lg-none mobile-stats-strip w-100"
					v-if="!$auth.loading && $auth.isAuthenticated"
				>
					<!-- 20260616 -- Copilot (Claude Sonnet 4.6) -- role/tabindex/keydown: make stat tiles keyboard-accessible -->
					<span
						class="mobile-stat-item"
						role="button"
						tabindex="0"
						aria-label="Today's tasks"
						@click="navigateToFilteredTasks('today')"
						@keydown.enter.prevent="navigateToFilteredTasks('today')"
						@keydown.space.prevent="navigateToFilteredTasks('today')"
					>
						<span class="mobile-stat-label">Today</span>
						<span class="mobile-stat-value">{{ stats.today }}</span>
					</span>
					<span
						class="mobile-stat-item"
						role="button"
						tabindex="0"
						aria-label="Tasks due in the next 7 days"
						@click="navigateToFilteredTasks('week')"
						@keydown.enter.prevent="navigateToFilteredTasks('week')"
						@keydown.space.prevent="navigateToFilteredTasks('week')"
					>
						<span class="mobile-stat-label">7 Days</span>
						<span class="mobile-stat-value">{{ stats.week }}</span>
					</span>
					<span
						class="mobile-stat-item"
						role="button"
						tabindex="0"
						aria-label="Tasks due in the next 30 days"
						@click="navigateToFilteredTasks('month')"
						@keydown.enter.prevent="navigateToFilteredTasks('month')"
						@keydown.space.prevent="navigateToFilteredTasks('month')"
					>
						<span class="mobile-stat-label">30 Days</span>
						<span class="mobile-stat-value">{{ stats.month }}</span>
					</span>
					<span
						class="mobile-stat-item"
						role="button"
						tabindex="0"
						aria-label="Task velocity score"
						:class="velocityClass"
						@click="handleVelocityClick"
						@keydown.enter.prevent="handleVelocityClick"
						@keydown.space.prevent="handleVelocityClick"
					>
						<span class="mobile-stat-label">Velocity</span>
						<span class="mobile-stat-value" :style="{ color: velocityColor }">{{ velocityScoreFormatted }}</span>
					</span>
				</div>

				<!-- Block 3: Desktop header widget (hidden below lg) — unchanged -->
				<b-navbar-nav
					:fill="true"
					class="d-none d-lg-flex header-nav-full">

					<div v-if="!$auth.loading && $auth.isAuthenticated" class="header-widget-expanded">

						<!-- Left Section: Logo + Greeting (unified group) -->
						<div class="header-left-group">
							<div class="logo-wrapper">
								<img
									:src="require('@/assets/logos/PTLogo-SVG-NoText-Cropped.svg')"
									alt="Logo"
									class="header-logo"
								/>
							</div>

							<div class="greeting-section">
								<div class="greeting-text">
									<p class="greeting-hi">{{timeGreeting}},</p>
									<p class="greeting-name">{{userPreferredName}}</p>
								</div>
							</div>
						</div>

						<!-- Center Section: Stats -->
				<!-- 20260126 -- Copilot (Claude Sonnet 4.5) -- Made stat cards clickable to filter tasks -->
				<!-- WHY: Allow users to click stats to view matching tasks on /tasks/custom -->
				<div class="header-stats-section">
					<!-- 20260616 -- Copilot (Claude Sonnet 4.6) -- role/tabindex/keydown: make desktop stat cards keyboard-accessible; remove inline cursor style (moved to .stat-card CSS) -->
					<div
						class="stat-card"
						role="button"
						tabindex="0"
						aria-label="Today's tasks"
						@click="navigateToFilteredTasks('today')"
						@keydown.enter.prevent="navigateToFilteredTasks('today')"
						@keydown.space.prevent="navigateToFilteredTasks('today')"
					>
						<div class="stat-label">Today</div>
						<div class="stat-value">{{ stats.today }}</div>
						<div class="stat-unit">tasks</div>
					</div>
					<div
						class="stat-card"
						role="button"
						tabindex="0"
						aria-label="Tasks due in the next 7 days"
						@click="navigateToFilteredTasks('week')"
						@keydown.enter.prevent="navigateToFilteredTasks('week')"
						@keydown.space.prevent="navigateToFilteredTasks('week')"
					>
						<div class="stat-label">7 Days</div>
						<div class="stat-value">{{ stats.week }}</div>
						<div class="stat-unit">tasks</div>
					</div>
					<div
						class="stat-card"
						role="button"
						tabindex="0"
						aria-label="Tasks due in the next 30 days"
						@click="navigateToFilteredTasks('month')"
						@keydown.enter.prevent="navigateToFilteredTasks('month')"
						@keydown.space.prevent="navigateToFilteredTasks('month')"
					>
						<div class="stat-label">30 Days</div>
						<div class="stat-value">{{ stats.month }}</div>
						<div class="stat-unit">tasks</div>
					</div>
				<!-- 20260206 -- Copilot (Claude Sonnet 4.5) -- Velocity score in header -->
				<div class="stat-card velocity-card" :class="velocityClass" role="button" tabindex="0" aria-label="Task velocity score" @click="handleVelocityClick" @keydown.enter.prevent="handleVelocityClick" @keydown.space.prevent="handleVelocityClick">
					<div class="stat-label">Velocity</div>
					<div class="stat-value" :style="{ color: velocityColor }">
						{{ velocityScoreFormatted }}
					</div>
					<div class="stat-unit">multiplier</div>
					</div>
						</div>

						<!-- Right Section: Quick Add + Avatar -->
						<div class="header-right-section">
							<div class="quick-add-section">
								<b-form-input
									v-model="quickAddTaskTitle"
									size="sm"
									class="quick-add-input"
									placeholder="What needs to be done?"
									@keyup.enter="quickAddTask"
								/>
								<b-button
									size="sm"
									class="quick-add-button"
									type="button"
									variant="success"
									@click="quickAddTask"
									title="Add a new task"
								>
									<b-icon-plus-circle class="button-icon" />
								</b-button>
							</div>

							<!-- Avatar Dropdown -->
							<b-nav-item-dropdown right class="avatar-dropdown-widget">
								<template v-slot:button-content>
									<b-avatar
										v-if="!($store.getters.getUser === null)"
										:src="$store.getters.getUser.picture"
										size="55px"
										class="header-avatar-widget"
									></b-avatar>
									<span v-else>User</span>
								</template>
								<b-dropdown-item :to="{name: 'profile'}">
									<b-icon-person />&nbsp;
									Profile
								</b-dropdown-item>

								<!-- 20260616 -- Copilot (Claude Sonnet 4.6) -- Simplified v-if: (!loading && !auth) || !auth always equals !auth -->
								<b-dropdown-item
									v-if="!$auth.isAuthenticated"
									:to="{name: 'signup'}"
								>
									<b-icon-lock /> &nbsp;
									Sign In
								</b-dropdown-item>

								<!-- 20260616 -- Copilot (Claude Sonnet 4.6) -- Removed href="#" to prevent hash navigation on logout -->
								<b-dropdown-item
									v-else
									@click="logout()"
								>
									<b-icon-lock-fill /> &nbsp;
									Log Out
								</b-dropdown-item>
							</b-nav-item-dropdown>
						</div>
					</div>

				</b-navbar-nav>

			</b-navbar>

			<!-- Premium Upgrade Modal for Velocity -->
			<PremiumUpgradeModal
				ref="velocityUpgradeModal"
				featureName="Task Velocity"
				featureDescription="Your rolling 7-day ratio of tasks completed vs. tasks created. Above 1.0x, you're ahead. Below 1.0x, your list is growing faster than you can clear it."
				:featureBenefits="['Daily velocity score (X.XXx format)', 'Created vs. completed chart', 'Filter by folder, context, priority, and status']"
				crossSell="Plus: Templates, Kanban board view"
			/>
			
		</b-col>
	</b-row>
</span>
</template>

<script>

import taskOps from '@/mixins/taskOps'
import genericOps from '@/mixins/genericOps'
import PremiumUpgradeModal from '@/components/PremiumUpgradeModal.vue'
import UserService from '@/services/UserService'
import Logger from '@/utils/Logger'

const log = Logger.create('UserMenu', { showCaller: true, showLineNumbers: true })

export default {
	name: 'UserMenu',
	components: { PremiumUpgradeModal },
	mixins: [taskOps, genericOps],
	data() {
		return {
			currentHour: new Date().getHours(),
			lastScrollPosition: 0,
			scrollThreshold: 100,
			headerHidden: false,
			stats: {
				today: 0,
				week: 0,
				month: 0
			},
			// 20260206 -- Copilot (Claude Sonnet 4.5) -- Velocity score
			velocityScore: 0
		}
	},
	async mounted() {
		// 20260125 -- Copilot (Claude Sonnet 4.5) -- Add scroll listener for header hide/show
		window.addEventListener('scroll', this.handleScroll);
		// 20260125 -- Copilot (Claude Sonnet 4.5) -- Load stats on mount
		await this.loadStats();
		// 20260615 -- Copilot (Claude Sonnet 4.6) -- Track navbar height with ResizeObserver so --mobile-header-height
		// and the spacer margin stay correct after viewport resizes (e.g. mobile → tablet crossing 992px).
		// One-shot $nextTick was enough at mount but broke when the two-row header collapsed mid-session.
		// 20260616 -- Copilot (Claude Sonnet 4.6) -- Also clear inline marginBottom when crossing into desktop (≥992px)
		// so a stale mobile value doesn't leave incorrect spacing after a resize from narrow to wide viewport.
		// 20260617 -- Copilot (Claude Sonnet 4.6) -- Scope both queries to this.$el; use .usermenu-spacer-row
		// instead of .row to avoid matching unrelated sibling rows if the component structure changes.
		this._applyNavbarHeight = () => {
			const nav = this.$el.querySelector('.usermenu-navbar')
			if (!nav) return
			const h = nav.offsetHeight
			document.documentElement.style.setProperty('--mobile-header-height', `${h}px`)
			const spacer = this.$el.querySelector('.usermenu-spacer-row')
			// 20260619 -- Copilot (Claude Sonnet 4.6) -- navbar is fixed="top" at all widths; spacer always needs the full navbar height
			if (spacer) spacer.style.marginBottom = `${h}px`
		}
		this.$nextTick(() => {
			this._applyNavbarHeight()
			const nav = this.$el.querySelector('.usermenu-navbar')
			if (nav && window.ResizeObserver) {
				this._navbarResizeObserver = new ResizeObserver(this._applyNavbarHeight)
				this._navbarResizeObserver.observe(nav)
			}
		})
	},
	beforeUnmount() {
		// 20260125 -- Copilot (Claude Sonnet 4.5) -- Clean up scroll listener
		window.removeEventListener('scroll', this.handleScroll);
		// 20260615 -- Copilot (Claude Sonnet 4.6) -- Disconnect navbar ResizeObserver
		if (this._navbarResizeObserver) this._navbarResizeObserver.disconnect()
	},
	computed: {
		// 20260206 -- Copilot (Claude Sonnet 4.5) -- Velocity score formatting
		velocityScoreFormatted() {
			return this.velocityScore > 0 ? `${this.velocityScore.toFixed(1)}x` : '--'
		},
		velocityColor() {
			if (this.velocityScore >= 1.0) return '#10b981' // Green
			if (this.velocityScore >= 0.8) return '#f59e0b' // Orange
			return '#ef4444' // Red
		},
		velocityClass() {
			if (this.velocityScore >= 1.0) return 'velocity-positive'
			if (this.velocityScore >= 0.8) return 'velocity-moderate'
			return 'velocity-low'
		},
		timeGreeting() {
			const hour = new Date().getHours();
			if (hour >= 5 && hour < 12) {
				return 'Good morning';
			} else if (hour >= 12 && hour < 17) {
				return 'Good afternoon';
			} else if (hour >= 17 && hour < 21) {
				return 'Good evening';
			} else {
				return 'Good night';
			}
		},
		userPreferredName() {
			const user = this.$store.getters.getUser;
			// Use goesBy if available, otherwise fall back to firstName, then Guest
			if (user) {
				if (user.goesBy && user.goesBy.trim() !== '') {
					return user.goesBy;
				} else if (user.firstName) {
					return user.firstName;
				}
			}
			return 'Guest';
		},
		isSubscriber() {
			const user = this.$store.getters.getUser;
			return user && user.subscriptionLevel && user.subscriptionLevel !== 'free';
		}
	},
	watch: {
		// 20260125 -- Copilot (Claude Sonnet 4.5) -- Reload stats when tasks change
		'$store.state.tasks': {
			handler() {
				this.loadStats();
			},
			deep: true
		}
	},
	methods: {
		// 20260206 -- Copilot (Claude Sonnet 4.5) -- Handle velocity stat click
		// WHY: Premium users navigate to dashboard, non-premium users see upgrade modal
		handleVelocityClick() {
			if (this.isPremiumUser()) {
				this.$router.push('/dashboard#task-velocity')
			} else {
				this.$refs.velocityUpgradeModal.show()
			}
		},
		// 20260126 -- Copilot (Claude Sonnet 4.5) -- Navigate to filtered task view
		// WHY: Allow users to click header stats to view matching tasks
		navigateToFilteredTasks(period) {
			const now = new Date()
			now.setHours(0, 0, 0, 0)
			const startOfToday = now.getTime()
			
			let filterItems = []
			let endMillis
			
			if (period === 'today') {
				// Today: start == today OR due == today
				endMillis = startOfToday
				
				// Group A: completed = N AND start = today
				filterItems.push({
					group: 'a',
					field: 'completed',
					operator: '$eq',
					value: 'N'
				})
				filterItems.push({
					group: 'a',
					field: 'start',
					operator: '$eq',
					value: endMillis
				})
				
				// Group B: completed = N AND due = today
				filterItems.push({
					group: 'b',
					field: 'completed',
					operator: '$eq',
					value: 'N'
				})
				filterItems.push({
					group: 'b',
					field: 'due',
					operator: '$eq',
					value: endMillis
				})
			} else if (period === 'week') {
				// 7 Days: start >= today AND start < today+8 OR due >= today AND due < today+8
				endMillis = startOfToday + (8 * 24 * 60 * 60 * 1000)
				
				// Group A: completed = N AND start in range
				filterItems.push({
					group: 'a',
					field: 'completed',
					operator: '$eq',
					value: 'N'
				})
				filterItems.push({
					group: 'a',
					field: 'start',
					operator: '$gte',
					value: startOfToday
				})
				filterItems.push({
					group: 'a',
					field: 'start',
					operator: '$lt',
					value: endMillis
				})
				
				// Group B: completed = N AND due in range
				filterItems.push({
					group: 'b',
					field: 'completed',
					operator: '$eq',
					value: 'N'
				})
				filterItems.push({
					group: 'b',
					field: 'due',
					operator: '$gte',
					value: startOfToday
				})
				filterItems.push({
					group: 'b',
					field: 'due',
					operator: '$lt',
					value: endMillis
				})
			} else if (period === 'month') {
				// 30 Days: start >= today AND start < today+30 OR due >= today AND due < today+30
				endMillis = startOfToday + (30 * 24 * 60 * 60 * 1000)
				
				// Group A: completed = N AND start in range
				filterItems.push({
					group: 'a',
					field: 'completed',
					operator: '$eq',
					value: 'N'
				})
				filterItems.push({
					group: 'a',
					field: 'start',
					operator: '$gte',
					value: startOfToday
				})
				filterItems.push({
					group: 'a',
					field: 'start',
					operator: '$lt',
					value: endMillis
				})
				
				// Group B: completed = N AND due in range
				filterItems.push({
					group: 'b',
					field: 'completed',
					operator: '$eq',
					value: 'N'
				})
				filterItems.push({
					group: 'b',
					field: 'due',
					operator: '$gte',
					value: startOfToday
				})
				filterItems.push({
					group: 'b',
					field: 'due',
					operator: '$lt',
					value: endMillis
				})
			}
			
			const filter = { filterItems, quickFilter: null }
			
			// 20260206 -- Copilot (Claude Sonnet 4.5) -- Use system filter route to preserve user's custom filter
			// WHY: Previously modified userPrefs.taskFilter, overwriting user's saved custom filter
			// BUG FIX: Use tasks-system-filter with state instead of modifying userPrefs
			this.$router.push({
				name: 'tasks-system-filter',
				state: { filter },
				query: { _t: Date.now() }
			})
		},
		// 20260125 -- Copilot (Claude Sonnet 4.5) -- Load user task statistics
		async loadStats() {
			try {
				if (this.$auth && !this.$auth.loading && this.$auth.isAuthenticated) {
					const token = await this.$auth.getTokenSilently()
					const response = await UserService.getUserStats(token)
					if (response.successful) {
						this.stats = {
							today: response.data.day || 0,
							week: response.data.week || 0,
							month: response.data.month || 0
						}
					}
					
					// 20260206 -- Copilot (Claude Sonnet 4.5) -- Load velocity score
					const userId = this.$store.getters.getUser._id
					const AnalyticsService = require('@/services/AnalyticsService').default
					const velocityResponse = await AnalyticsService.getDashboardVelocity(userId, 30, { metric: 'created' }, token)
					if (velocityResponse.data.response && velocityResponse.data.response.successful) {
						this.velocityScore = velocityResponse.data.response.data.velocityScore || 0
					}
				}
			} catch (error) {
				log.error('Failed to load stats:', error)
				// Keep default zeros on error
			}
		},
		// 20260125 -- Copilot (Claude Sonnet 4.5) -- Handle scroll to hide/show header
		handleScroll() {
			const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
			
			// Ignore small scroll movements
			if (Math.abs(currentScrollPosition - this.lastScrollPosition) < 5) {
				return;
			}
			
			// Show header when at top
			if (currentScrollPosition < this.scrollThreshold) {
				this.headerHidden = false;
			}
			// Hide when scrolling down
			else if (currentScrollPosition > this.lastScrollPosition) {
				this.headerHidden = true;
			}
			// Show when scrolling up
			else {
				this.headerHidden = false;
			}
			
			this.lastScrollPosition = currentScrollPosition;
		},
		/**
		 * handleSidebarToggle - Relay mobile hamburger tap to AppLayout via root event bus
		 * CREATED: 20260615 | WHO: Copilot (Claude Sonnet 4.6) | WHY: UserMenu owns the mobile navbar; AppLayout owns the sidebar state
		 */
		handleSidebarToggle() {
			this.$root.$emit('toggle:sidebar')
		},
		login(goTo) {
        
        		let uri = ''

        		if(goTo) {

          		uri = window.location + goTo

        		} else {
          		uri = window.location.origin
        		}

        		this.$auth.loginWithRedirect({
          		redirect_uri: uri
        		});
		},
		logout() {
			this.$auth.logout({
				returnTo: process.env.VUE_APP_ENV_LOGOUT_URL || 'https://www.principaltask.com'
			});
		}
	}
}
</script>

<style scoped>
/* 20260616 -- Copilot (Claude Sonnet 4.6) -- Spacer row; actual height driven by _applyNavbarHeight (ResizeObserver) */
/* Replaces the old hardcoded inline style="margin-bottom:125pt" which conflicted with dynamic height tracking */
.usermenu-spacer-row {
	position: relative;
}

.navbar-brand-text {
	font-size: 1.5rem;
	font-weight: 600;
	color: #ffffff;
	vertical-align: middle;
}

.logo-container {
	display: flex;
	align-items: center;
}

.logo-wrapper {
	flex-shrink: 0;
	flex-basis: 55px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 0;
	width: 55px;
	height: 55px;
}

.header-logo {
	width: 55px !important;
	height: 55px !important;
	display: block !important;
	max-width: 55px !important;
	max-height: 55px !important;
	min-width: 55px !important;
	min-height: 55px !important;
	flex-shrink: 0 !important;
	object-fit: contain;
	object-position: center;
}

.navbar-brand-text {
	font-size: 1.5rem;
	font-weight: 700;
	color: #ffffff;
	vertical-align: middle;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* ============================================
   NAVBAR BASE STYLES
   ============================================ */

.usermenu-navbar {
	transition: transform 0.3s ease;
}

.usermenu-navbar.header-hidden {
	transform: translateY(-100%);
}

/* ============================================
   FEATURED EXPANDED HEADER WIDGET
   ============================================ */

.expanded-navbar {
	padding: 0.25rem 1rem !important;
	height: auto;
}

.header-nav-full {
	width: 100%;
	margin-left: 0 !important;
}

.header-widget-expanded {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	width: 98%;
	max-width: none;
	margin: 0 auto;
	padding: 0.4rem 1.5rem;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%);
	border: 1px solid rgba(120, 203, 91, 0.2);
	border-radius: 12px;
	backdrop-filter: blur(10px);
	transition: all 0.3s ease;
}

/* Left Group: Logo + Greeting unified */
.header-left-group {
	display: flex;
	align-items: center;
	flex: 0 1 auto;
	min-width: auto;
	gap: 0.75rem;
}

/* Greeting Section */
.greeting-section {
	display: flex;
	align-items: center;
	flex-shrink: 0;
}

.greeting-text {
	margin: 0;
}

.greeting-hi {
	font-size: 0.8rem;
	color: rgba(255, 255, 255, 0.6);
	font-weight: 500;
	margin: 0;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.greeting-name {
	font-size: 1.3rem;
	font-weight: 700;
	color: #ffffff;
	margin: 0.2rem 0 0 0;
}

/* Quick Add Section */
.quick-add-section {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-shrink: 0;
}

.quick-add-input {
	background: rgba(255, 255, 255, 0.1) !important;
	border: 1px solid rgba(255, 255, 255, 0.15) !important;
	color: #ffffff !important;
	font-size: 0.95rem;
	padding: 0.65rem 1rem !important;
	transition: all 0.3s ease;
	border-radius: 8px;
	width: 200px;
	min-width: 150px;
}

.quick-add-input::placeholder {
	color: rgba(255, 255, 255, 0.5) !important;
	font-weight: 400;
}

.quick-add-input:focus {
	background: rgba(255, 255, 255, 0.15) !important;
	border-color: #78CB5B !important;
	box-shadow: 0 0 0 3px rgba(120, 203, 91, 0.15) !important;
	outline: none;
}

.quick-add-button {
	padding: 0.5rem 1rem !important;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	white-space: nowrap;
	font-weight: 600;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(120, 203, 91, 0.25);
	flex-shrink: 0;
}

.quick-add-button:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 16px rgba(120, 203, 91, 0.35);
}

.quick-add-button:active {
	transform: translateY(0);
	box-shadow: 0 2px 8px rgba(120, 203, 91, 0.25);
}

.button-icon {
	font-size: 1.1rem;
}

/* Avatar Dropdown Widget */
.avatar-dropdown-widget {
	flex-shrink: 0;
}

.avatar-dropdown-widget:hover .header-avatar-widget {
	transform: scale(1.1);
	filter: brightness(1.2);
}

.header-avatar-widget {
	transition: all 0.3s ease;
	cursor: pointer;
}

.avatar-dropdown-widget::v-deep .btn-link {
	cursor: pointer;
}

/* Right Section: Quick Add + Avatar */
.header-right-section {
	display: flex;
	align-items: center;
	gap: 1rem;
	flex: 0 1 auto;
}

.avatar-wrapper-widget {
	display: flex;
	align-items: center;
	padding: 0.4rem;
	background: rgba(120, 203, 91, 0.1);
	border: 2px solid rgba(120, 203, 91, 0.3);
	border-radius: 50%;
	cursor: pointer;
	transition: all 0.3s ease;
	user-select: none;
}

.avatar-wrapper-widget:hover {
	background: rgba(120, 203, 91, 0.2);
	border-color: #78CB5B;
	box-shadow: 0 0 16px rgba(120, 203, 91, 0.3);
	transform: scale(1.08);
}

/* Stats Section Styling */
.header-stats-section {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 1rem;
	flex: 0 1 auto;
}

.stat-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 0.5rem 1.25rem;
	background: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(120, 203, 91, 0.2);
	border-radius: 12px;
	text-align: center;
	transition: all 0.3s ease;
	min-width: 85px;
	cursor: pointer;
}

.stat-card:hover {
	background: rgba(120, 203, 91, 0.1);
	border-color: rgba(120, 203, 91, 0.4);
	transform: translateY(-3px);
	box-shadow: 0 4px 16px rgba(120, 203, 91, 0.15);
}

.stat-label {
	font-size: 0.75rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: rgba(255, 255, 255, 0.65);
	margin-bottom: 0.35rem;
}

.stat-value {
	font-size: 1.75rem;
	font-weight: 700;
	color: #78CB5B;
	line-height: 1;
	margin-bottom: 0.25rem;
}

.stat-unit {
	font-size: 0.7rem;
	color: rgba(255, 255, 255, 0.5);
	font-weight: 500;
}

/* 20260615 -- Copilot (Claude Sonnet 4.6) -- Mobile/tablet navbar layout */
/* WHY: Removed b-collapse + its linked b-navbar-toggle; toggleable="lg" is kept because */
/* it adds navbar-expand-lg, which correctly hides .navbar-toggler above 992px. */

/* navbar-expand-lg sets flex-wrap:nowrap at lg+; override to wrap so mobile rows stack */
.expanded-navbar {
	flex-wrap: wrap;
}

@media (min-width: 992px) {
	.expanded-navbar {
		flex-wrap: nowrap;
	}
}

.mobile-nav-row {
	padding: 0.35rem 0;
}

.mobile-sidebar-toggle {
	color: rgba(255, 255, 255, 0.85);
	border: 1px solid rgba(255, 255, 255, 0.35);
	border-radius: 6px;
	padding: 0.25rem 0.6rem;
	font-size: 1.4rem;
	background: transparent;
	cursor: pointer;
	flex-shrink: 0;
	line-height: 1;
}

.mobile-stats-strip {
	justify-content: space-around;
	border-top: 1px solid rgba(255, 255, 255, 0.1);
	padding: 0.4rem 0.5rem;
}

.mobile-stat-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	cursor: pointer;
	padding: 0.2rem 0.5rem;
	border-radius: 6px;
	transition: background 0.2s;
}

.mobile-stat-item:hover,
.mobile-stat-item:active {
	background: rgba(255, 255, 255, 0.08);
}

.mobile-stat-label {
	font-size: 0.65rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.4px;
	color: rgba(255, 255, 255, 0.6);
}

.mobile-stat-value {
	font-size: 1.1rem;
	font-weight: 700;
	color: #78CB5B;
	line-height: 1.1;
}
</style>
